import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  searchVehicles,
  type FactionProfileView,
  type VehicleHit,
  type VehiclePage,
} from '../api/catalog';
import { ca } from '../i18n/ca';
import { AssetPicture } from './AssetPicture';

/** How many arrive at once. Sixty fills a wide screen twice and no phone at all. */
const PAGE = 60;

/**
 * The whole mod set's vehicles, browsed.
 *
 * Until this existed the quick start offered the vehicles a squad starts with
 * as one `select` of the chosen faction's own list: no picture, no way to reach
 * anything the faction does not field, and — on a mod set with CUP or RHS in
 * it — several hundred lines in one drop-down. Three things were wrong with
 * that, and this fixes them together: the game's own picture of every class,
 * the catalogue whole rather than one faction's corner of it, and a way in that
 * goes category first and shows nothing until something is asked for.
 *
 * The order is deliberate. Nothing is listed on opening: the categories are
 * offered with their counts, and a list appears when a category is picked or a
 * name is typed. That is the difference between a browser and a dump, and it is
 * why the server pages this rather than sending the whole CfgVehicles down.
 *
 * Nothing of anybody else's art travels with the platform: every picture here is
 * {@link AssetPicture}, which asks the panel for the copy the host it runs
 * beside already owns, and draws nothing when there is none.
 */
export function VehiclePicker({
  modSetHash,
  factions,
  faction: initialFaction,
  onPick,
  onClose,
}: {
  modSetHash: string;
  /** The mod set's factions, for the filter; the picker never fetches them itself. */
  factions: FactionProfileView[];
  /** Where to start looking: ours, usually. Empty is the whole catalogue. */
  faction?: string | null;
  onPick: (vehicle: VehicleHit) => void;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const [faction, setFaction] = useState<string>(initialFaction ?? '');
  /** null is "nothing asked for yet"; the empty string is "every category". */
  const [kind, setKind] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [total, setTotal] = useState(0);
  const [list, setList] = useState<VehicleHit[]>([]);
  const [busy, setBusy] = useState(false);

  const kindNames = ca.missionUi.ours.vehicleKinds;
  const t = ca.missionUi.vehiclePicker;
  const showing = kind !== null || query.length >= 2;

  // A search of one letter matches everything and costs a round trip for it; two
  // is where a name starts being a name. What was typed keeps its own state, so
  // the field never waits for the network.
  useEffect(() => {
    const id = setTimeout(() => setQuery(typed.trim()), 250);
    return () => clearTimeout(id);
  }, [typed]);

  // Typing is already a narrowing, so it shows what it finds instead of asking
  // for a category first.
  useEffect(() => {
    if (query.length >= 2) setKind((k) => (k === null ? '' : k));
  }, [query]);

  const receive = useCallback((page: VehiclePage) => {
    setCounts(page.kinds ?? {});
    setTotal(page.total);
    setList(page.vehicles ?? []);
  }, []);

  useEffect(() => {
    let live = true;
    setBusy(true);
    searchVehicles(modSetHash, {
      faction: faction || null,
      kind: kind || null,
      q: query.length >= 2 ? query : null,
      // Before a category is picked only the counts are wanted, and a page of
      // sixty classes nobody asked for is a page nobody should be sent.
      limit: kind === null ? 0 : PAGE,
    })
      .then((page) => {
        if (live) receive(page);
      })
      .catch(() => {
        if (live) receive({ modSetHash, total: 0, kinds: {}, vehicles: [] });
      })
      .finally(() => {
        if (live) setBusy(false);
      });
    return () => {
      live = false;
    };
  }, [modSetHash, faction, kind, query, receive]);

  async function more() {
    setBusy(true);
    try {
      const page = await searchVehicles(modSetHash, {
        faction: faction || null,
        kind: kind || null,
        q: query.length >= 2 ? query : null,
        offset: list.length,
        limit: PAGE,
      });
      setList((current) => [...current, ...(page.vehicles ?? [])]);
      setTotal(page.total);
    } catch {
      // A page that does not arrive leaves the ones that did, and the button.
    } finally {
      setBusy(false);
    }
  }

  // Escape closes, the way every other dialog here closes, and the search field
  // takes the focus because typing a name is the fastest way through a catalogue
  // of two thousand classes. Tab stays inside: a modal that lets it wander
  // behind is not modal.
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    panel.current?.querySelector<HTMLInputElement>('input[type="search"]')?.focus();
    return () => before?.focus?.();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panel.current) return;
      const stops = [
        ...panel.current.querySelectorAll<HTMLElement>('button, [href], input, select'),
      ].filter((el) => !(el as HTMLButtonElement).disabled);
      if (stops.length === 0) return;
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // The mod set's factions under the side they fight for, so the filter reads
  // like the faction cards above it rather than like a hash table.
  const bySide = useMemo(() => {
    const out = new Map<string, FactionProfileView[]>();
    for (const f of [...factions].sort((a, b) => a.name.localeCompare(b.name, 'ca'))) {
      out.set(f.side, [...(out.get(f.side) ?? []), f]);
    }
    return out;
  }, [factions]);

  const kinds = Object.keys(counts);

  return (
    <div
      className="confirm-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="vehicle-picker"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vehicle-picker-title"
      >
        <header className="row">
          <h2 id="vehicle-picker-title">{t.title}</h2>
          <button type="button" className="small" onClick={onClose}>
            {t.close}
          </button>
        </header>

        <div className="filters">
          <label>
            {t.search}
            <input
              type="search"
              name="vehicleSearch"
              placeholder={t.searchPlaceholder}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>
          <label>
            {t.faction}
            <select
              name="vehicleFaction"
              value={faction}
              onChange={(e) => setFaction(e.target.value)}
            >
              <option value="">{t.allFactions}</option>
              {[...bySide.entries()].map(([side, sideFactions]) => (
                <optgroup key={side} label={ca.sideNames[side] ?? side}>
                  {sideFactions.map((f) => (
                    <option key={f.id} value={f.factionClass}>
                      {f.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
        </div>

        <div className="tabs">
          <button
            type="button"
            aria-pressed={kind === ''}
            className={kind === '' ? 'active' : ''}
            onClick={() => setKind('')}
          >
            {t.anyKind}
          </button>
          {kinds.map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={kind === k}
              className={kind === k ? 'active' : ''}
              onClick={() => setKind(k)}
            >
              {kindNames[k] ?? k}
              <span className="chip">{counts[k]}</span>
            </button>
          ))}
        </div>

        <div className="found">
          {!showing && <p className="muted small">{t.start}</p>}
          {showing && list.length === 0 && !busy && (
            <p className="muted small">{kinds.length === 0 ? t.noCatalogue : t.empty}</p>
          )}
          {showing && list.length > 0 && (
            <ul className="cards grid pick vehicles">
              {list.map((v) => (
                <li key={v.className}>
                  <button type="button" className="card" onClick={() => onPick(v)}>
                    <AssetPicture
                      thumb
                      size={140}
                      assetClass={v.className}
                      modSetHash={modSetHash}
                      picture={v.picture}
                      name={v.displayName}
                    />
                    <strong>{v.displayName}</strong>
                    <span className="muted small">{v.factionName || v.factionClass}</span>
                    <span className="row wrap">
                      <span className="chip">{kindNames[v.kind] ?? v.kind}</span>
                      {v.transportSoldier > 0 && (
                        <span className="chip">{t.seats(v.transportSoldier)}</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="row">
          <span className="muted small">
            {busy ? t.loading : showing ? t.shown(list.length, total) : ''}
          </span>
          {showing && list.length < total && (
            <button type="button" disabled={busy} onClick={() => void more()}>
              {t.more}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
